import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { BookmarkIcon } from 'lucide-react';
import { ProjectTag } from '@/hooks/content-generator/workbook-types';
import { useTagMutations } from '@/hooks/content-generator/use-tags-query';
import { colorOptions } from './tag-color-options';

interface TagFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialTag?: ProjectTag;
}

type FormValues = {
  name: string;
  color: string;
};

const TagFormDialog: React.FC<TagFormDialogProps> = ({ 
  isOpen, 
  onClose, 
  mode, 
  initialTag 
}) => {
  const { register, handleSubmit, watch, setValue, formState: { errors, isValid } } = useForm<FormValues>({
    defaultValues: {
      name: initialTag?.name || '',
      color: initialTag?.color || colorOptions[0].value
    }
  });
  
  const { createTag, updateTag, isPending } = useTagMutations();
  
  const selectedColor = watch('color');
  
  const onSubmit = async (data: FormValues) => {
    if (mode === 'create') {
      createTag({
        name: data.name,
        color: data.color,
        // Remove userId - it will be handled by the backend automatically
      });
    } else if (mode === 'edit' && initialTag) {
      updateTag({
        id: initialTag.id,
        updates: {
          name: data.name,
          color: data.color,
        }
      });
    }
    
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Category' : 'Edit Category'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              placeholder="Enter category name"
              {...register('name', { required: true })}
            />
            {errors.name && (
              <p className="text-xs text-red-500">Category name is required</p>
            )}
          </div>
          
          <div className="space-y-3">
            <label className="text-sm font-medium">Color</label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((option) => (
                <div 
                  key={option.value}
                  className={`${option.value} w-8 h-8 rounded-full cursor-pointer transition-transform
                    ${selectedColor === option.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                  onClick={() => setValue('color', option.value, { shouldValidate: true })}
                ></div>
              ))}
            </div>

          </div>
          
          <div className="flex justify-end gap-3">
            <Button type="button" variant="cancel" size="cancel" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!isValid || isPending}
            >
              {isPending ? 'Saving...' : mode === 'create' ? 'Create Category' : 'Update Category'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TagFormDialog;
